/* eslint camelcase: "off" */
/**
 * Identity Verifications — fraud-review queue for ID-photo uploads.
 *
 * Surfaces every ID-photo VerificationDocument with its fraud severity +
 * raw fraud signals. Defaults to "medium and above" so the noise of
 * clean matched uploads doesn't bury actual review work.
 *
 * The queue does NOT show the extracted ID number — that's PII fetched
 * separately via the per-document detail endpoint with an explicit
 * X-Access-Reason header. The list is intentionally a triage view.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DateRangePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tag,
} from '@jem-open/jem-ui';
import { Eye, Loader2 } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import {
  FraudSeverity,
  IdentityVerificationListItem,
  verificationClient,
} from '../../../../services/verificationClient';
import DocumentsAuditTab from './identityVerifications/DocumentsAuditTab';
import AccessAuditTab from './identityVerifications/AccessAuditTab';
import InvestigationsTab from './identityVerifications/InvestigationsTab';
import PeopleTab from './identityVerifications/PeopleTab';
import SessionsListTab from './identityVerifications/SessionsListTab';
import { meaningfulSignals, SIGNAL_SHORT_LABEL } from './identityVerifications/signals';


// Same severity → tag-variant mapping as the bench tab. Kept independent
// (vs imported from the bench) because the bench is a temp file we'll
// delete; this one is the production fraud-review surface.
function severityVariant(
  severity: FraudSeverity | null | undefined
): 'success' | 'pending' | 'processing' | 'failed' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'failed';
    case 'medium':
      return 'pending';
    case 'low':
      return 'processing';
    case 'ok':
      return 'success';
    case 'unknown':
    default:
      return 'processing';
  }
}

const SEVERITY_OPTIONS: Array<{ value: FraudSeverity | '_all'; label: string }> = [
  { value: 'critical', label: 'critical and above' },
  { value: 'high', label: 'high and above (default)' },
  { value: 'medium', label: 'medium and above' },
  { value: 'low', label: 'low and above' },
  { value: 'ok', label: 'all (incl. clean)' },
  { value: '_all', label: 'all' },
];

const STATUS_OPTIONS = [
  { value: '_all', label: 'All statuses' },
  { value: 'mismatch', label: 'mismatch (fraud signal)' },
  { value: 'extraction_failed', label: 'extraction_failed' },
  { value: 'ocr_passed', label: 'ocr_passed (matched)' },
  { value: 'rejected', label: 'rejected' },
  { value: 'collected', label: 'collected (pending analysis)' },
];

// Reviewer-verdict colour mapping. Mirrors the django ReviewOutcome enum
// — see verification/models/verification_document.py. The "noise" /
// "evidence overrides" colour split keeps the table visually scannable:
// red = bad, green = cleared, amber = action required.
const REVIEW_OUTCOME_VARIANT: Record<string, 'success' | 'failed' | 'pending' | 'processing'> = {
  confirm_fraud: 'failed',
  cleared: 'success',
  accepted_with_override: 'processing',
  recapture_required: 'pending',
  inconclusive: 'pending',
  escalate: 'failed',
};


const DOC_KIND_OPTIONS = [
  { value: '_all', label: 'Any kind' },
  { value: 'sa_id', label: 'SA ID' },
  { value: 'passport', label: 'Passport' },
];


type DateRange = { from: Date | undefined; to?: Date };


// 'sessions'  — NEW per-session rollup (one row per flow_session_id).
// 'uploads'   — the legacy per-VerificationDocument fraud queue (used
//               to be the 'sessions' tab; the name was misleading
//               because each row is one upload, not one session).
type SubTab = 'sessions' | 'uploads' | 'patterns' | 'employees' | 'documents' | 'access';


const IdentityVerificationsTab: React.FC = () => {
  const history = useHistory();
  const [subTab, setSubTab] = useState<SubTab>('sessions');
  const [employeeIdFilter, setEmployeeIdFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState<FraudSeverity | '_all'>('high');
  const [statusFilter, setStatusFilter] = useState<string>('_all');
  const [docKindFilter, setDocKindFilter] = useState<string>('_all');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const [rows, setRows] = useState<IdentityVerificationListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Cursor is passed in explicitly per call. Reading it from component
  // state inside the callback would close over a stale value on the
  // synchronous "Load more" path (setCursor + load fire in the same
  // tick), so the next page would refetch the previous one.
  const load = useCallback(
    async (resetCursor: boolean, cursorOverride?: string | null) => {
      setLoading(true);
      try {
        const params: Parameters<typeof verificationClient.listIdentityVerifications>[0] = {};
        if (employeeIdFilter) params.employee_id = parseInt(employeeIdFilter, 10);
        if (severityFilter !== '_all') params.severity_at_least = severityFilter;
        if (statusFilter !== '_all') params.status = statusFilter;
        if (docKindFilter !== '_all') params.document_kind = docKindFilter as 'sa_id' | 'passport';
        if (dateRange?.from) params.since = dateRange.from.toISOString();
        if (dateRange?.to) params.until = dateRange.to.toISOString();
        if (!resetCursor && cursorOverride) params.cursor = cursorOverride;
        const page = await verificationClient.listIdentityVerifications(params);
        setRows((prev) => (resetCursor ? page.results : [...prev, ...page.results]));
        setNextCursor(page.next_cursor);
      } catch (err) {
        apiResponseHandler.handleApiError(err);
      } finally {
        setLoading(false);
      }
    },
    [employeeIdFilter, severityFilter, statusFilter, docKindFilter, dateRange]
  );

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeIdFilter, severityFilter, statusFilter, docKindFilter, dateRange]);

  const handleReset = () => {
    setDateRange(null);
    setEmployeeIdFilter('');
    setSeverityFilter('high');
    setStatusFilter('_all');
    setDocKindFilter('_all');
  };

  const displayRows = rows;

  return (
    <div>
      <Tabs value={subTab} onValueChange={(val) => setSubTab(val as SubTab)}>
        <TabsList className="mb-sm">
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="uploads">Uploads</TabsTrigger>
          <TabsTrigger value="patterns">Patterns</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="access">Access</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <SessionsListTab />
        </TabsContent>

        <TabsContent value="uploads">
      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-xs">
        <div>
          <DateRangePicker
            label="Date range"
            dateRange={dateRange ?? undefined}
            onDateRangeChange={(range) => setDateRange((range as DateRange | undefined) ?? null)}
            placeholder="Pick a date range"
          />
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Employee ID</span>
          <Input
            type="number"
            className="w-[160px]"
            value={employeeIdFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmployeeIdFilter(e.target.value)
            }
            placeholder="e.g. 102837"
          />
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Severity</span>
          <Select
            value={severityFilter}
            onValueChange={(val) => setSeverityFilter(val as FraudSeverity | '_all')}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEVERITY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Document kind</span>
          <Select value={docKindFilter} onValueChange={setDocKindFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_KIND_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={handleReset}>
          Reset
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Captured</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Signals</TableHead>
              <TableHead>Review</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((row) => {
              const signals = meaningfulSignals(row.fraud_signals);
              return (
              <React.Fragment key={row.id}>
                <TableRow>
                  <TableCell className="text-xs">
                    {new Date(row.captured_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-medium">
                      {row.employee_name || (
                        <span className="text-greyscale-text-caption">—</span>
                      )}
                    </div>
                    <div className="text-xxs text-greyscale-text-caption">
                      {row.employer_name ? `${row.employer_name} · ` : ''}#{row.employee_id}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{row.document_kind || '—'}</TableCell>
                  <TableCell>
                    <Tag
                      variant={
                        row.verification_status === 'mismatch'
                          ? 'failed'
                          : row.verification_status === 'ocr_passed' ||
                            row.verification_status === 'face_matched'
                          ? 'success'
                          : 'pending'
                      }
                    >
                      {row.verification_status}
                    </Tag>
                  </TableCell>
                  <TableCell>
                    <Tag variant={severityVariant(row.fraud_severity)}>
                      {row.fraud_severity || 'unknown'}
                    </Tag>
                  </TableCell>
                  <TableCell className="text-xs">{row.extraction_method || '—'}</TableCell>
                  <TableCell>
                    {signals.length === 0 ? (
                      <span className="text-xxs text-greyscale-text-caption">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-xxs">
                        {signals.map(({ key, value, sev }) => (
                          <Tag key={key} variant={severityVariant(sev)}>
                            {SIGNAL_SHORT_LABEL[key]}: {String(value)}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.review_outcome ? (
                      <Tag variant={REVIEW_OUTCOME_VARIANT[row.review_outcome] || 'pending'}>
                        {row.review_outcome.replace(/_/g, ' ')}
                      </Tag>
                    ) : (
                      <span className="text-xxs text-greyscale-text-caption">—</span>
                    )}
                    {row.reviewer_note && (
                      <div
                        className="mt-xxs max-w-xs truncate text-xxs italic text-greyscale-text-caption"
                        title={row.reviewer_note}
                      >
                        &quot;{row.reviewer_note}&quot;
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      size="xs"
                      onClick={() =>
                        row.flow_session_id
                          ? history.push(
                              `/admin/security/sessions/${row.flow_session_id}`
                            )
                          : null
                      }
                      disabled={!row.flow_session_id}
                      title={
                        row.flow_session_id ? 'Open session detail' : 'No flow session linked'
                      }
                      leftIcon={<Eye size={14} />}
                    >
                      view
                    </Button>
                  </TableCell>
                </TableRow>
              </React.Fragment>
              );
            })}
            {displayRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} className="py-md text-center text-greyscale-text-caption">
                  No identity verifications match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
        {nextCursor && (
          <div className="flex justify-center py-sm">
            <Button
              variant="link"
              onClick={() => load(false, nextCursor)}
              disabled={loading}
            >
              Load more →
            </Button>
          </div>
        )}
      </div>
        </TabsContent>

        <TabsContent value="patterns">
          <InvestigationsTab />
        </TabsContent>

        <TabsContent value="employees">
          <PeopleTab />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsAuditTab />
        </TabsContent>

        <TabsContent value="access">
          <AccessAuditTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IdentityVerificationsTab;
